import { useCallback, useMemo } from 'react';
import { LuceraBlotterItem } from '../../model/blotters';
import Divider from '../common/Divider';

import { convertedQtyFormatterConfig } from '@/config/config';
import { createLumeFxRestClient } from '@/services/LumeFxService';
import { useAppSelector } from '@/state/hooks';
import { useInstrument } from '@/utils/hooks/useInstrument';
import useLanguage from '@/utils/hooks/useLanguage';
import { useLucera } from '@/utils/providers/LuceraProvider';
import cn from 'classnames';
import { useNumberFormatter } from 'react-aria';
import { selectCredentials } from '../../state/reducers/authSlice';
import Modal, { ModalHeader, ModalProps, ModalTitle } from './Modal';

interface OrderCancellationModalProps extends ModalProps {
    order1: LuceraBlotterItem;
    order2?: LuceraBlotterItem;
    onSubmit?: () => void;
}

const luceraRestURL = window.config?.lucera?.rest;

const OrderCancellationModal = (props: OrderCancellationModalProps) => {
    const { order1, order2, handlers, onSubmit } = props;
    const { orderType } = order1;

    const { jwt } = useLucera();
    const lang = useLanguage();
    const credentials = useAppSelector(selectCredentials);

    const { pip_size, config } = useInstrument(order1.pair);
    const formatNumber = useNumberFormatter(convertedQtyFormatterConfig);

    const lumeFxRestClient = createLumeFxRestClient({
        baseUrl: luceraRestURL,
        authorization: jwt
    });

    const { isStopMarket, isStopLimit } = useMemo(
        () => ({
            isStopMarket: orderType === 'Stop',
            isStopLimit: orderType === 'StopLimit'
        }),
        [orderType]
    );

    const order1PriceType = useMemo(
        () => (isStopMarket || isStopLimit ? lang.orderForm.stopPrice : lang.orderForm.limitPrice),
        [lang, orderType]
    );

    const order2PriceType = useMemo(
        () => (order2?.orderType === 'Stop' ? lang.orderForm.stopPrice : lang.orderForm.limitPrice),
        [lang, order2?.orderType]
    );

    const onCancelOrder = useCallback(() => {
        if (order1 && credentials) {
            try {
                if (order2) {
                    // const order2Payload = {
                    //     orderId: order2.orderId,
                    //     clOrdId: order2.clientOrderId,
                    //     origClOrdId: order2.originalClientOrderId,
                    //     onBehalfOfCompId: order2.onBehalfOfCompId,
                    //     deliverToCompId: order2.deliverToCompId,
                    //     checkpointTimestamp: order2.checkpointTimestamp,
                    //     onBehalfOfUsername: '',
                    //     parentOrderId: order2.parentOrderId
                    // };

                    lumeFxRestClient.cancelOrder({ clordid: order1.clientOrderId });
                    // cancelLimitOrder(order2Payload, credentials);
                } else {
                    lumeFxRestClient.cancelOrder({ clordid: order1.clientOrderId });
                }
            } catch (error) {
                console.error('Error cancelling order:', error);
            }
            // onSubmit();
            handlers.close();
        }
    }, [order1, credentials]);

    return (
        <Modal {...props} className="h-auto" size={order2 ? 'max-w-lg' : 'max-w-md'}>
            <ModalHeader>
                <ModalTitle>{lang.orderForm.orderCancellation}</ModalTitle>
            </ModalHeader>
            <Divider className="bg-brand-red" />
            <div className="text-brand-text p-2 sm:p-4">
                <div className="pb-4 text-sm">
                    <div className="flex flex-col space-y-2">
                        {order2 && (
                            <div className="space-y-3">
                                <div className="flex flex-col gap-1">
                                    {/* <h3 className="text-md font-bold pb-1 text-xs">
                                        {order1.metadata.find(({ key }) => key === 'PARENT_STRATEGY_ID')?.value}
                                    </h3> */}
                                    <div>
                                        {lang.blotters.side} : {order1.side}
                                    </div>
                                    <div>
                                        {lang.orderForm.symbolName} :{' '}
                                        {config.type === 'Future' ? config.gcexSymbol : order1.pair}
                                    </div>
                                    <div>
                                        {lang.orderForm.duration} : {order1.tif}
                                    </div>
                                </div>
                                <div
                                    className={cn('flex gap-3', {
                                        'flex-row-reverse': isStopMarket || isStopLimit
                                    })}>
                                    <div className="space-y-1 w-full border border-brand-neutral-600 rounded-sm p-2">
                                        <div>
                                            {lang.blotters.orderId} : {order1.orderId}
                                        </div>
                                        <div>
                                            {lang.orderForm.orderType} : {order1.orderType}
                                        </div>
                                        <div>
                                            {lang.blotters.status} : {order1.status}
                                        </div>
                                        <div>
                                            {order1PriceType} :{' '}
                                            {formatNumber.format(
                                                (isStopMarket || isStopLimit ? order1.price : order1.price) || 0
                                            )}
                                        </div>
                                        <div>
                                            {lang.orderForm.quantity} : {formatNumber.format(order1.qty || 0)}
                                        </div>
                                        {isStopLimit && (
                                            <>
                                                {/* <div>
                                                    {lang.orderForm.slippage} :{' '}
                                                    {convertToPips(order1.slippage, pip_size)}
                                                </div> */}
                                                <div>
                                                    {lang.orderForm.stopLimitPrice} : {order1.price}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="space-y-1 w-full border border-brand-neutral-600 rounded-sm p-2">
                                        <div>
                                            {lang.blotters.orderId} : {order2.orderId}
                                        </div>
                                        <div>
                                            {lang.orderForm.orderType} : {order2.orderType}
                                        </div>
                                        <div>
                                            {lang.blotters.status} : {order2.status}
                                        </div>
                                        <div>
                                            {order2PriceType} :{' '}
                                            {formatNumber.format(
                                                (order2?.orderType === 'Stop' ? order2.price : order2.price) || 0
                                            )}
                                        </div>
                                        <div>
                                            {lang.orderForm.quantity} : {formatNumber.format(order2.qty || 0)}
                                        </div>
                                        {order2?.orderType === 'StopLimit' && (
                                            <>
                                                {/* <div>
                                                    {lang.orderForm.slippage} :{' '}
                                                    {convertToPips(order2.slippage, pip_size)}
                                                </div> */}
                                                <div>
                                                    {lang.orderForm.stopLimitPrice} : {order2.price}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {!order2 && (
                            <div className="space-y-1">
                                <h3 className="text-md font-bold pb-1">Order Details</h3>
                                <div>
                                    {lang.orderForm.symbolName} :{' '}
                                    {config.type === 'Future' ? config.gcexSymbol : order1.pair}
                                </div>
                                <div>
                                    {lang.blotters.side} : {order1.side}
                                </div>
                                <div>
                                    {lang.orderForm.orderType} : {order1.orderType}
                                </div>
                                <div>
                                    {lang.blotters.orderId} : {order1.orderId}
                                </div>
                                <div>
                                    {lang.blotters.status} : {order1.status}
                                </div>
                                <div>
                                    {lang.orderForm.duration} : {order1.tif}
                                </div>
                                <div>
                                    {order1PriceType} :{' '}
                                    {formatNumber.format(
                                        (isStopMarket || isStopLimit ? order1.price : order1.price) || 0
                                    )}
                                </div>
                                <div>
                                    {lang.orderForm.quantity} : {formatNumber.format(order1.qty || 0)}
                                </div>
                                {isStopLimit && (
                                    <>
                                        {/* <div>
                                            {lang.orderForm.slippage} : {convertToPips(order1.slippage, pip_size)}
                                        </div> */}
                                        <div>
                                            {lang.orderForm.stopLimitPrice} : {order1.price}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="text-sm">
                    {order2
                        ? lang.orderForm.areYouSureYouWantToCancelBothOrders
                        : lang.orderForm.areYouSureYouWantToCancelThisOrder}
                </div>
            </div>
            <Divider className="bg-brand-red" />
            <div className="text-brand-text p-2 sm:p-4 text-sm flex space-x-2">
                <button
                    className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                    onClick={handlers.close}>
                    {lang.common.back}
                </button>
                <button className="rounded-md p-2 px-4 bg-brand-red hover:bg-brand-red-light" onClick={onCancelOrder}>
                    {order2 ? lang.orderForm.cancelBothOrders : lang.orderForm.cancelOrder}
                </button>
            </div>
        </Modal>
    );
};

export default OrderCancellationModal;
